// Story 7-FU-F (ADO-1219, AC1) — the header depot picker.
//
// The FIRST consumer of the session store's depot model (depotIds / activeDepot
// / setActiveDepot, shipped by 6.3/6.16 but wired to nothing until now). Picking
// a depot writes `activeDepot`; the availability layer reads it as the session
// depot (see session-depot.ts) and `useAvailability` reacts to the change —
// re-fetching badges and moving the real-time socket to the new depot's room.
//
// Built on the DS `Select` (native <select> + <label htmlFor>) so keyboard /
// screen-reader behaviour and the ≥44px touch target come for free (Story 7.7).
// Degrades by cardinality: 0 depots → render nothing (availability stays off,
// AC6); exactly 1 → a read-only label (OQ-4); ≥2 → the interactive picker.
// Story 7.22 (ADO-1703, resolving D-5 / OQ-2) — labels are the REAL depot name
// from `useSessionStore.depotNames` (the session read), falling back to the id
// when a name has not resolved (never a fabricated "VC ..." placeholder).

import { useEffect, type ChangeEvent } from 'react';
import { Select } from '../design-system';
import { depotNameFor, useSessionStore } from '../auth/useSessionStore';
import { useSubstitutesStore } from '../substitutes/useSubstitutesStore';
import { useRecommendationsStore } from '../recommendations/useRecommendationsStore';
import styles from './DepotPicker.module.css';

export function DepotPicker() {
  const depotIds = useSessionStore((s) => s.depotIds);
  const activeDepot = useSessionStore((s) => s.activeDepot);
  const setActiveDepot = useSessionStore((s) => s.setActiveDepot);
  const depotNames = useSessionStore((s) => s.depotNames);
  const nameFor = (id: string): string => depotNameFor(depotNames, id);

  // Story 7-FU (ADO-1750), AC3 — de-duplicate before ANY cardinality decision or
  // render, preserving first-seen order. `mapServerSessionToContext` now dedupes
  // `depotIds` at the store (the authoritative fix, shared with the SiteHeader
  // picker), so this is defense-in-depth: it keeps `DepotPicker` from emitting
  // duplicate React keys or mis-counting cardinality even when state is set directly
  // (a test seed, or any future path that bypasses the mapper). Every branch below
  // judges cardinality on this deduped list.
  const uniqueDepotIds = [...new Set(depotIds)];

  // Story 7-FU (ADO-1750), AC3 — reconcile a STALE active depot INTO the available
  // list. After an in-session claims refresh drops the previously-active depot,
  // `activeDepot` is a valid id no longer in `depotIds`; left alone `resolveSessionDepotId`
  // keeps resolving a depot the user no longer holds while the picker shows a
  // different one (they diverge, and the native <select> cannot be corrected by
  // re-picking the already-shown depot). Clamp the STORE to the first remaining depot
  // so the display, the store and the resolution all agree. Fires whenever ANY depot
  // remains (`unique.length > 0`), so the single-remaining-depot case is reconciled too,
  // not just multi-depot. Gated on a NON-NULL active depot so it never auto-selects for a
  // user who has not yet picked: that null state is the Story 8.1 add gate, and the picker
  // shows the "Select" placeholder to prompt the pick (see `selectedValue`). Resets the
  // depot-scoped substitutes/recommendations caches, exactly as a manual switch does
  // (`handleChange`), because the reconcile is a real depot switch: otherwise a
  // previously-viewed SKU keeps the dropped depot's suggestions on screen. A post-render
  // effect, never a state write during render. Runs before the cardinality early-returns
  // to keep hook order stable.
  useEffect(() => {
    const unique = [...new Set(depotIds)];
    if (activeDepot !== null && unique.length > 0 && !unique.includes(activeDepot)) {
      useSubstitutesStore.getState().reset();
      useRecommendationsStore.getState().reset();
      setActiveDepot(unique[0] ?? null);
    }
  }, [depotIds, activeDepot, setActiveDepot]);

  // No depots (unauthenticated / empty `depotIds` from the server session):
  // availability is disabled, so the control does not render at all (AC1/AC6).
  if (uniqueDepotIds.length === 0) return null;

  // A single depot has nothing to pick — show it read-only so the user still
  // sees which depot their stock reflects, without an inert dropdown (OQ-4).
  // Always the sole remaining depot: a stale non-null `activeDepot` (dropped by a
  // claims refresh) must never be shown for the frame before the effect above
  // reconciles the store, so this reads the list, not `activeDepot`.
  if (uniqueDepotIds.length === 1) {
    const only = uniqueDepotIds[0] ?? '';
    return (
      <div className={styles.readonly}>
        <span className={styles.readonlyLabel}>Depot</span>
        <span className={styles.readonlyValue}>{nameFor(only)}</span>
      </div>
    );
  }

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (next === activeDepot) return;
    // A live depot switch invalidates the per-SKU substitutes/recommendations
    // caches too: both are depot-scoped and read the same session depot, but are
    // driven lazily by load(sku) with a `loaded` early-return, so without this a
    // previously-viewed SKU keeps showing the OLD depot's suggestions after a
    // switch while its availability badge re-resolves (7-FU-F review). The
    // availability store self-resets inside loadForSkus; these two do not.
    useSubstitutesStore.getState().reset();
    useRecommendationsStore.getState().reset();
    setActiveDepot(next);
  };

  // Story 7-FU (ADO-1750), AC3 — the controlled value:
  //   • `null` (multi-depot user has not picked, or a refresh cleared it): show the
  //     empty placeholder. This is the Story 8.1 unselected state — the add gate
  //     blocks and the picker must PROMPT a pick, never claim a depot is active.
  //   • an in-list depot: show it selected (the normal case).
  //   • a stale non-null depot: fall back to the first depot for the one frame before
  //     the effect above reconciles the store, so no placeholder flash.
  // Never `resolveSessionDepotId` (its null-for-unselected result is the Story 8.1
  // gate); `handleChange` still owns the real switch.
  const selectedValue =
    activeDepot === null
      ? ''
      : uniqueDepotIds.includes(activeDepot)
        ? activeDepot
        : (uniqueDepotIds[0] ?? '');

  return (
    <div className={styles.picker}>
      <Select
        label="Depot"
        options={uniqueDepotIds.map((id) => ({ value: id, label: nameFor(id) }))}
        value={selectedValue}
        onChange={handleChange}
      />
    </div>
  );
}
